import * as React from 'react'
import { KeyRound, Loader2, Mail, MoveLeft } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BrandMark } from '@/components/brand-mark'
import { relativeTime } from '@/lib/format'
import { MAIL_EXAMPLE_CODE, MAIL_FROM_ADDRESS, MAIL_FROM_NAME, MAIL_SUBJECT } from '@/lib/mail'

/**
 * Passwordless sign-in.
 *
 * The emailed message carries BOTH a link and a 6-digit code, and we support
 * both here. That's deliberate rather than belt-and-braces: the link is the
 * nice path, but it only works in the browser that opened it, and GoTrue
 * builds it from the tenant's external URL — so on a phone, or behind a mail
 * client that rewrites links, the code is the one that always works.
 */
export function SignIn() {
  const [stage, setStage] = React.useState<'email' | 'sent'>('email')
  const [email, setEmail] = React.useState('')
  const [code, setCode] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [notice, setNotice] = React.useState<string | null>(null)
  const [sentAt, setSentAt] = React.useState<Date | null>(null)

  // Re-render every 10s so "sent 40 seconds ago" keeps counting. Someone staring
  // at this screen waiting for mail is exactly who needs it to stay truthful,
  // and a frozen "just now" is what makes people resend three times.
  const [, tick] = React.useReducer((n: number) => n + 1, 0)
  React.useEffect(() => {
    if (stage !== 'sent') return
    const t = window.setInterval(tick, 10_000)
    return () => window.clearInterval(t)
  }, [stage])

  async function sendLink(e: React.FormEvent) {
    e.preventDefault()
    const address = email.trim().toLowerCase()
    if (!address) return
    setBusy(true)
    setError(null)
    try {
      const { error: err } = await sb().auth.signInWithOtp({
        email: address,
        options: {
          // Where the link returns to. Our /verify route exchanges the token.
          emailRedirectTo: `${window.location.origin}/verify`,
          // Anyone may REQUEST a link; access is decided by team membership
          // once they're in (see <NoAccess/>). Creating the auth user here is
          // what lets an invited person sign in without a pre-provisioned
          // account.
          shouldCreateUser: true,
        },
      })
      if (err) throw err
      setSentAt(new Date())
      setStage('sent')
      setNotice(null)
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault()
    const token = code.replace(/\D/g, '')
    if (token.length < 6) {
      setError('Enter the 6-digit code from the email.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const address = email.trim().toLowerCase()

      // Which OTP "type" the code was issued under depends on whether this
      // address was already a user when the link was requested: an existing
      // user gets a magiclink token, a brand-new one (shouldCreateUser: true)
      // gets a signup token. The client cannot know which, and GoTrue answers a
      // type mismatch with the *same* "expired or invalid" it uses for a real
      // expiry — so a single hard-coded type fails 100% of the time for one
      // group of users and looks exactly like an expiry bug.
      //
      // So try each. A failed verify does not consume the token, and we stop at
      // the first error that another type couldn't fix.
      let lastError: unknown = null
      for (const type of ['email', 'magiclink', 'signup'] as const) {
        const { error: err } = await sb().auth.verifyOtp({ email: address, token, type })
        if (!err) {
          // The auth listener in AuthProvider takes over and the router swaps
          // this screen for the app — nothing more to do here.
          return
        }
        lastError = err
        if (!worthRetryingWithAnotherType(err)) break
      }
      throw lastError
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  async function resend() {
    setBusy(true)
    setError(null)
    try {
      const { error: err } = await sb().auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { emailRedirectTo: `${window.location.origin}/verify`, shouldCreateUser: true },
      })
      if (err) throw err
      setSentAt(new Date())
      setNotice('Sent another link.')
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center text-center">
          <BrandMark className="h-11 w-11" />
          <h1 className="mt-4 text-xl font-semibold tracking-tight">Team Accelerator</h1>
          <p className="text-sm text-muted-foreground">Don&apos;t Panic Labs Partners</p>
        </div>

        <Card>
          {stage === 'email' ? (
            <>
              <CardHeader>
                <CardTitle className="text-base">Sign in</CardTitle>
                <CardDescription>
                  We&apos;ll email you a sign-in link. No password to remember.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={sendLink} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Work email</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      required
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  <Button type="submit" className="w-full" disabled={busy}>
                    {busy ? <Loader2 className="animate-spin" /> : <Mail />}
                    Email me a link
                  </Button>
                </form>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-base">Check your email</CardTitle>
                <CardDescription>
                  We sent a sign-in link to <span className="font-medium text-foreground">{email}</span>.
                  Click it, or enter the 6-digit code below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Who it's from and when it went — the two things someone
                    digging through a spam folder actually needs, and the two
                    that decide whether they wait or give up and resend. */}
                <dl className="space-y-1.5 rounded-md border bg-muted/40 px-3 py-2.5 text-xs">
                  <div className="flex gap-2">
                    <dt className="w-12 shrink-0 text-muted-foreground">From</dt>
                    {/* Both halves of the real From header. The name is what the
                        inbox list shows, so it goes first; the address is what
                        you can paste into a mailbox search, so it keeps the
                        emphasis. Stacked rather than "Name <addr>" because that
                        one line wraps badly in a narrow card. */}
                    <dd className="min-w-0">
                      {MAIL_FROM_NAME && (
                        <span className="block text-muted-foreground">{MAIL_FROM_NAME}</span>
                      )}
                      <span className="block break-all font-medium">{MAIL_FROM_ADDRESS}</span>
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-12 shrink-0 text-muted-foreground">Subject</dt>
                    <dd className="min-w-0 font-medium">{MAIL_SUBJECT}</dd>
                  </div>
                  {sentAt && (
                    <div className="flex gap-2">
                      <dt className="w-12 shrink-0 text-muted-foreground">Sent</dt>
                      <dd className="min-w-0">
                        <span className="font-medium">
                          {sentAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                        </span>
                        <span className="text-muted-foreground">
                          {' · '}
                          {relativeTime(sentAt.toISOString())}
                        </span>
                      </dd>
                    </div>
                  )}
                </dl>

                {/* An illustration of the message, not the message. Nothing here
                    is a live link — the point is to make an unfamiliar email
                    recognizable at a glance in a crowded inbox. */}
                <div aria-hidden className="select-none rounded-md border border-dashed px-3 py-3">
                  <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    What it looks like
                  </p>
                  <p className="text-sm font-semibold">Magic Link</p>
                  <p className="mt-1.5 text-xs text-muted-foreground">Follow this link to login:</p>
                  <p className="mt-1 text-xs font-medium text-primary underline underline-offset-2">Log In</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Alternatively, enter the code:{' '}
                    <span className="font-mono font-medium tracking-wider text-foreground">
                      {MAIL_EXAMPLE_CODE}
                    </span>
                  </p>
                </div>

                <form onSubmit={submitCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">6-digit code</Label>
                    <Input
                      id="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      maxLength={7}
                      // No numeric placeholder: the example above already shows a
                      // six-digit code, and a greyed "123456" sitting in the field
                      // reads as already-filled-in. Dashes show the shape without
                      // impersonating a value.
                      placeholder="------"
                      className="text-center font-mono text-lg tracking-[0.35em]"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
                  <Button type="submit" className="w-full" disabled={busy}>
                    {busy ? <Loader2 className="animate-spin" /> : <KeyRound />}
                    Sign in
                  </Button>
                </form>
                <div className="flex items-center justify-between text-sm">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setStage('email')
                      setCode('')
                      setError(null)
                      setNotice(null)
                    }}
                  >
                    <MoveLeft /> Use another email
                  </Button>
                  <Button variant="link" size="sm" onClick={resend} disabled={busy}>
                    Resend
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Access is by invitation. If you haven&apos;t been added to a team yet, ask your Don&apos;t
          Panic Labs coach.
        </p>
        {/* The one link out of the sign-in screen. Someone who cannot get in
            needs to know whether that is them or us, and /status answers it
            without a session — a plain anchor rather than a router Link
            because this screen renders outside the Routes tree. */}
        <p className="mt-2 text-center text-xs text-muted-foreground">
          <a href="/status" className="underline underline-offset-2 hover:text-foreground">
            System status
          </a>
        </p>
      </div>
    </div>
  )
}

/**
 * A token-lookup miss — which is what the wrong `type` produces, and what a
 * genuine expiry produces, in identical wording. Worth another type; a rate
 * limit or a dead connection is not (and each retry spends the same limit).
 */
function worthRetryingWithAnotherType(err: unknown): boolean {
  const raw = err instanceof Error ? err.message : String(err)
  if (/rate limit|too many/i.test(raw)) return false
  if (/failed to fetch|network/i.test(raw)) return false
  return /expired|invalid|not found/i.test(raw)
}

/** GoTrue's raw errors are terse; translate the ones users actually hit. */
function messageFor(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err)
  // GoTrue says "Token has expired or is invalid" for a genuinely old token AND
  // for one that simply doesn't match — most often a code from an earlier email
  // after a Resend superseded it. Reporting that as "expired" sends people to
  // Resend, which supersedes the code again: a loop that never resolves. Name
  // the likely cause instead of picking one half of GoTrue's own ambiguity.
  if (/expired or (is )?invalid/i.test(raw)) {
    return 'That code didn’t work. If you asked for more than one email, only the code in the newest one works — try that, or request a new code.'
  }
  if (/expired/i.test(raw)) return 'That code or link has expired. Request a new one.'
  if (/invalid|not found/i.test(raw)) return 'That code doesn’t look right. Check the email and try again.'
  if (/rate limit|too many/i.test(raw)) return 'Too many attempts. Wait a minute and try again.'
  if (/failed to fetch|network/i.test(raw)) return 'Could not reach the server. Check your connection.'
  return raw
}
